import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost, apiPut } from '../lib/api';
import { clearAuth } from '../lib/storage';
import { toast } from 'sonner';
import {
  CheckCircle2, Circle, Mail, Users, X, ArrowRight, Loader2,
  SkipForward, UserCircle2, Compass, FileText, Download,
  AlertTriangle, Phone, ExternalLink, LogOut,
} from 'lucide-react';

const STATUS_KEY = 'ellora_onboarding_status';
const DISMISS_KEY = 'ellora_onboarding_dismissed';

/**
 * Onboarding Wizard
 * -----------------
 * Two flows based on role:
 *
 *  • SUPER_ADMIN (workspace setup) — 2 steps:
 *      1. SMTP Email Setup
 *      2. Add First Employee
 *
 *  • REGULAR USER (self-onboarding) — 3 steps:
 *      1. Welcome & Profile (Phone + Emergency Contact MANDATORY · safety compliance)
 *      2. Quick Tour (top modules the user has access to · primary CTA per module)
 *      3. Acknowledge HR Policies (ALL mandatory · gates the close button)
 *
 *  Note: password change is enforced at LOGIN (Login.jsx) — it is NOT an
 *  onboarding step anymore (showing an already-done step served no purpose).
 *
 *  Regular-user re-trigger: wizard re-appears on every login until all 3 steps complete.
 *  Super_admin: once dismissed, stays dismissed (relaunch from Settings).
 */
export default function OnboardingWizard({ user, onClose }) {
  const isSuperAdmin = user?.role === 'super_admin';
  return isSuperAdmin ? <SuperAdminWizard user={user} onClose={onClose} /> : <RegularUserWizard user={user} onClose={onClose} />;
}

// =====================================================================
// REGULAR USER FLOW — 3 steps · mandatory completion (no permanent dismiss)
// =====================================================================
function RegularUserWizard({ user, onClose }) {
  const navigate = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  const [profile, setProfile] = useState(null);
  const [noEmployee, setNoEmployee] = useState(false);
  const [pendingPolicies, setPendingPolicies] = useState([]);
  const [status, setStatus] = useState({});

  // Load profile + pending policies on mount; compute completion flags.
  // `autoAdvance` should be true only on the very first load — subsequent
  // reloads (e.g. after acknowledging a policy) must NOT re-jump the user
  // back to step 0 just because some earlier step's data hasn't been
  // refreshed yet. Without this guard the wizard "rewinds" to stage 1 every
  // time a policy is acknowledged.
  const reload = useCallback(async (autoAdvance = false) => {
    const [me, pendRes] = await Promise.all([
      apiGet('/hr/me').catch(() => null),
      apiGet('/hr/policies/me/pending').catch(() => ({ pending: [] })),
    ]);
    const emp = me?.employee || null;
    setProfile(emp);
    // `me` is truthy even when no employee is linked (it returns {linked:false}).
    // Distinguish "loaded but unlinked" from "still loading / network error" so
    // we never trap a user on an impossible Profile step.
    setNoEmployee(me ? !emp : false);
    setPendingPolicies(pendRes?.pending || []);

    const next = {
      profile: !!(emp && emp.phone && emp.emergency_contact),
      tour: JSON.parse(localStorage.getItem(STATUS_KEY) || '{}').tour === true,
      policies: (pendRes?.pending || []).length === 0,
    };
    setStatus(next);
    localStorage.setItem(STATUS_KEY, JSON.stringify(next));
    if (autoAdvance) {
      // Auto-advance to first incomplete step (only on initial mount).
      if (!next.profile) setStepIdx(0);
      else if (!next.tour) setStepIdx(1);
      else if (!next.policies) setStepIdx(2);
    }
  }, []);

  useEffect(() => { reload(true); }, [reload]);

  // When no employee record is linked, the Profile step is impossible to
  // satisfy (PUT /hr/me would 404). Treat it as not-applicable so the user can
  // still finish the other steps and is never trapped behind an un-closeable
  // modal (which previously also blocked the logout button).
  const profileSatisfied = status.profile || noEmployee;
  const allDone = profileSatisfied && status.tour && status.policies;

  const tryClose = () => {
    if (!allDone) {
      toast.error('Please complete all 3 steps (Profile · Tour · Policies) before closing.');
      return;
    }
    localStorage.setItem(DISMISS_KEY, '1');
    onClose?.();
  };

  // Always-available escape hatch — a user must be able to sign out from any
  // screen, even a mandatory wizard.
  const handleLogout = async () => {
    try { await apiPost('/auth/logout', {}); } catch { /* ignore */ }
    await clearAuth();
    navigate('/login');
    setTimeout(() => window.location.reload(), 50);
  };

  const STEPS = [
    { id: 'profile', title: 'Complete Profile', icon: UserCircle2 },
    { id: 'tour', title: 'Quick Tour', icon: Compass },
    { id: 'policies', title: 'HR Policies', icon: FileText },
  ];
  const completedCount = STEPS.filter(s => (s.id === 'profile' ? profileSatisfied : status[s.id])).length;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-start justify-center p-4 sm:p-8 overflow-y-auto" data-testid="onboarding-wizard">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-3xl my-auto border border-slate-200">
        {/* Header */}
        <div className="flex items-start justify-between p-6 pb-4 border-b border-slate-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-display font-bold mb-1">
              Getting Started · {completedCount}/3
            </div>
            <h2 className="text-2xl font-display font-extrabold text-slate-800">Welcome, {user?.name?.split(' ')[0] || 'there'}!</h2>
            <p className="text-sm text-slate-500 mt-1">A 3-step setup to make sure your profile and compliance are in order.</p>
          </div>
          <button onClick={tryClose} data-testid="onboarding-close-btn" className={`p-1 ${allDone ? 'text-slate-400 hover:text-slate-700' : 'text-slate-300 cursor-not-allowed'}`} title={allDone ? 'Close' : 'Finish all 3 steps to close'}>
            <X size={20} />
          </button>
        </div>

        {/* Step indicator */}
        <StepBar steps={STEPS} stepIdx={stepIdx} setStepIdx={setStepIdx} status={{ ...status, profile: profileSatisfied }} />

        {/* Body */}
        <div className="p-6">
          {stepIdx === 0 && <ProfileStep profile={profile} onSaved={() => { reload(); setStepIdx(1); }} onSkip={() => setStepIdx(1)} />}
          {stepIdx === 1 && <TourStep user={user} done={status.tour} navigate={navigate} onDone={() => { const n = { ...status, tour: true }; setStatus(n); localStorage.setItem(STATUS_KEY, JSON.stringify(n)); setStepIdx(2); }} />}
          {stepIdx === 2 && <PoliciesStep pending={pendingPolicies} onAck={reload} />}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-4 border-t border-slate-200 bg-slate-50 text-xs">
          <button
            onClick={handleLogout}
            data-testid="onboarding-logout-btn"
            className="inline-flex items-center gap-1.5 text-slate-500 hover:text-red-600 font-display font-semibold"
          >
            <LogOut size={14} /> Log out
          </button>
          {allDone ? (
            <button onClick={tryClose} data-testid="onboarding-finish-btn" className="bg-[#1F7A6E] hover:bg-[#14504E] text-white font-display font-semibold text-sm px-4 py-2 rounded-sm inline-flex items-center gap-2">
              Take me in <ArrowRight size={14} />
            </button>
          ) : (
            <span className="text-slate-500 text-right">
              {noEmployee
                ? 'No HR profile is linked to your login — complete the remaining steps to continue, and ask HR to link your employee record.'
                : '⚠ Complete the steps above to close this wizard.'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ───── Regular user · Step 1: Profile (Phone + Emergency Contact MANDATORY)
function ProfileStep({ profile, onSaved, onSkip }) {
  const [form, setForm] = useState({
    phone: '', emergency_contact: '', address: '', dob: '', blood_group: '', gender: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (profile) {
      setForm({
        phone: profile.phone || '',
        emergency_contact: profile.emergency_contact || '',
        address: profile.address || '',
        dob: profile.dob || '',
        blood_group: profile.blood_group || '',
        gender: profile.gender || '',
      });
    }
  }, [profile]);

  if (!profile) {
    return (
      <div className="text-center py-8" data-testid="profile-step-no-employee">
        <AlertTriangle size={32} className="mx-auto text-amber-500 mb-2" />
        <div className="text-sm font-semibold text-slate-800">No employee record linked yet</div>
        <p className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
          Your login is active but no employee profile is linked to your email. Please ask HR to add you to the Employee Master with this exact email so attendance, leaves and payroll get linked.
        </p>
        <p className="text-xs text-slate-400 mt-2">You can still continue and finish the remaining steps.</p>
        <div className="flex justify-center mt-4">
          <button onClick={onSkip} data-testid="onb-profile-skip-btn"
            className="bg-[#1F7A6E] hover:bg-[#14504E] text-white font-display font-semibold text-sm px-4 py-2 rounded-sm flex items-center gap-2">
            Continue <ArrowRight size={14} />
          </button>
        </div>
      </div>
    );
  }

  const save = async () => {
    if (!form.phone || !form.emergency_contact) {
      toast.error('Phone and Emergency Contact are MANDATORY for safety compliance');
      return;
    }
    setSaving(true);
    try {
      await apiPut('/hr/me', form);
      toast.success('Profile updated');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="profile-step">
      <div>
        <div className="text-sm font-semibold text-slate-800 mb-1">Complete your profile</div>
        <p className="text-xs text-slate-500">Phone and Emergency Contact are required for safety compliance under our HSE policy.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Phone *" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} placeholder="+91 9876543210" testid="onb-profile-phone" />
        <Field label="Emergency Contact *" value={form.emergency_contact} onChange={(v) => setForm({ ...form, emergency_contact: v })} placeholder="Spouse / Parent · +91 9876543210" testid="onb-profile-emergency" />
        <Field label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} placeholder="House / Street / City" testid="onb-profile-address" />
        <Field label="Date of Birth" type="date" value={form.dob} onChange={(v) => setForm({ ...form, dob: v })} testid="onb-profile-dob" />
        <SelectField label="Blood Group" value={form.blood_group} onChange={(v) => setForm({ ...form, blood_group: v })} testid="onb-profile-blood"
          options={['', 'A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-']} />
        <SelectField label="Gender" value={form.gender} onChange={(v) => setForm({ ...form, gender: v })} testid="onb-profile-gender"
          options={['', 'Male', 'Female', 'Other', 'Prefer not to say']} />
      </div>
      <div className="flex justify-end pt-2 border-t border-slate-100">
        <button onClick={save} disabled={saving} data-testid="onb-profile-save-btn"
          className="bg-[#1F7A6E] hover:bg-[#14504E] text-white font-display font-semibold text-sm px-4 py-2 rounded-sm flex items-center gap-2 disabled:opacity-50">
          {saving ? <Loader2 size={14} className="animate-spin" /> : null}
          Save & Continue <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}

// ───── Regular user · Step 2: Quick Tour (cards for modules the user has access to)
function TourStep({ user, done, navigate, onDone }) {
  const allowed = user?.allowed_modules || [];
  // Curated CTAs per module — show only those the user has access to.
  const CATALOG = [
    { module: 'hr', title: 'HR & Attendance', desc: 'Mark attendance, apply for leaves, view your payslips.', path: '/app/hr/me', cta: 'Open my ESS' },
    { module: 'crm', title: 'Business Development', desc: 'Manage leads, opportunities, companies and contacts.', path: '/app/crm/leads', cta: 'View Pipeline' },
    { module: 'procurement', title: 'Procurement', desc: 'MPR / RFQ / PO workflow, vendor master, inventory.', path: '/app/procurement', cta: 'Open Procurement' },
    { module: 'hse', title: 'Safety / HSE', desc: 'Incidents, toolbox talks, permits, inspections.', path: '/app/hse', cta: 'Open HSE' },
    { module: 'finance', title: 'Finance', desc: 'AR/AP, P&L, cash flow, budgets.', path: '/app/finance', cta: 'Open Finance' },
    { module: 'dashboard', title: 'Executive Dashboard', desc: 'KPIs, production and dispatch health, action items.', path: '/app', cta: 'Open Dashboard' },
  ];
  const cards = CATALOG.filter(c => allowed.includes(c.module)).slice(0, 6);

  return (
    <div className="space-y-4" data-testid="tour-step">
      <div>
        <div className="text-sm font-semibold text-slate-800 mb-1">Here's what you can do</div>
        <p className="text-xs text-slate-500">A quick tour of the modules you have access to based on your role · <strong>{user?.role}</strong>. Click any tile to jump straight in.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {cards.length === 0 ? (
          <div className="col-span-2 text-center text-xs text-slate-400 py-6">No modules assigned. Ask your administrator to set your access.</div>
        ) : cards.map(c => (
          <button
            key={c.module}
            onClick={() => { onDone(); navigate(c.path); }}
            data-testid={`tour-card-${c.module}`}
            className="text-left bg-white border border-slate-200 rounded-sm p-4 hover:border-[#1F7A6E]/60 hover:shadow-sm group transition-all"
          >
            <div className="text-sm font-display font-bold text-slate-800 mb-1">{c.title}</div>
            <p className="text-xs text-slate-500 leading-relaxed mb-2">{c.desc}</p>
            <span className="text-[11px] text-[#1F7A6E] font-semibold inline-flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
              {c.cta} <ExternalLink size={11} />
            </span>
          </button>
        ))}
      </div>
      <div className="flex justify-end pt-2 border-t border-slate-100">
        <button onClick={onDone} data-testid="onb-tour-skip-btn" className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 px-3 py-2 mr-2">
          <SkipForward size={12} /> Skip tour
        </button>
        <button onClick={onDone} data-testid="onb-tour-continue-btn"
          className="bg-[#1F7A6E] hover:bg-[#14504E] text-white font-display font-semibold text-sm px-4 py-2 rounded-sm flex items-center gap-2">
          Continue <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );
}

// ───── Regular user · Step 3: HR Policies (ALL mandatory)
function PoliciesStep({ pending, onAck }) {
  const [acking, setAcking] = useState(null);
  const [openedSet, setOpenedSet] = useState(() => new Set());
  const [pdfView, setPdfView] = useState(null); // { policy, blobUrl, loading }

  if (!pending || pending.length === 0) {
    return (
      <div className="text-center py-8" data-testid="policies-step-done">
        <CheckCircle2 size={36} className="mx-auto text-emerald-500 mb-2" />
        <div className="text-sm font-semibold text-slate-800">All HR policies acknowledged</div>
        <p className="text-xs text-slate-500 mt-1">You can find them anytime in the Company Policies section.</p>
      </div>
    );
  }

  const open = async (p) => {
    // In-app authed PDF viewer — works on iOS Safari/PWA where window.open replaces the tab.
    setPdfView({ policy: p, blobUrl: null, loading: true });
    try {
      const token = localStorage.getItem('access_token') || '';
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hr/policies/${p.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      setPdfView({ policy: p, blobUrl, loading: false });
      setOpenedSet(prev => new Set([...prev, p.id]));
    } catch (e) {
      setPdfView(null);
      toast.error('Could not load the policy PDF');
    }
  };

  const closePdf = () => {
    if (pdfView?.blobUrl) URL.revokeObjectURL(pdfView.blobUrl);
    setPdfView(null);
  };

  const ack = async (p) => {
    if (!openedSet.has(p.id)) {
      toast.error('Please open and read the policy before acknowledging');
      return;
    }
    setAcking(p.id);
    try {
      await apiPost(`/hr/policies/${p.id}/acknowledge`, {});
      toast.success(`Acknowledged: ${p.title}`);
      onAck();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Acknowledge failed');
    } finally { setAcking(null); }
  };

  return (
    <div className="space-y-4" data-testid="policies-step">
      <div>
        <div className="text-sm font-semibold text-slate-800 mb-1">Read and acknowledge company policies</div>
        <p className="text-xs text-slate-500">
          You must read and digitally acknowledge ALL {pending.length} pending {pending.length === 1 ? 'policy' : 'policies'} before closing this wizard. Your acknowledgement is timestamped and stored as a compliance record.
        </p>
      </div>
      <div className="space-y-2 max-h-[360px] overflow-y-auto pr-1">
        {pending.map(p => (
          <div key={p.id} className="border border-slate-200 rounded-sm p-3 flex flex-wrap items-center gap-3" data-testid={`policy-${p.id}`}>
            <FileText size={20} className="text-[#1F7A6E] shrink-0" />
            <div className="flex-1 min-w-[200px]">
              <div className="text-sm font-semibold text-slate-800">{p.title}</div>
              <div className="text-[10px] text-slate-500 uppercase tracking-widest">
                v{p.version || '1'} · {p.category || 'General'}
              </div>
            </div>
            <button
              onClick={() => open(p)}
              data-testid={`policy-open-${p.id}`}
              className="flex items-center gap-1 px-3 py-1.5 border border-slate-200 text-xs rounded-sm hover:bg-slate-50"
            >
              <Download size={12} /> {openedSet.has(p.id) ? 'Re-open' : 'Read PDF'}
            </button>
            <button
              onClick={() => ack(p)}
              disabled={acking === p.id || !openedSet.has(p.id)}
              data-testid={`policy-ack-${p.id}`}
              className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white text-xs rounded-sm hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed font-display font-semibold"
              title={openedSet.has(p.id) ? 'Acknowledge this policy' : 'Open the PDF first'}
            >
              {acking === p.id ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
              I Acknowledge
            </button>
          </div>
        ))}
      </div>

      {/* In-app PDF viewer modal — works on iOS Safari + PWA.
          Iter 124 — header gets safe-area-inset-top padding so the Back /
          Acknowledge buttons clear the iPhone Dynamic Island / notch and
          remain clickable when the page is launched as a PWA (Add to
          Home Screen) on iOS. */}
      {pdfView && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex flex-col" data-testid="policy-pdf-modal">
          <div
            className="flex items-center justify-between bg-white border-b border-slate-200 px-3 pb-2.5 gap-2"
            style={{ paddingTop: 'calc(0.625rem + env(safe-area-inset-top))' }}
          >
            <button
              onClick={closePdf}
              data-testid="policy-pdf-back"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider bg-[#1F7A6E] text-white rounded-sm hover:bg-[#14504E]"
            >
              <ArrowRight size={12} className="rotate-180" /> Back to Acknowledge
            </button>
            <div className="text-xs font-display font-bold uppercase tracking-wider truncate flex-1 text-center text-slate-700">
              {pdfView.policy.title}
            </div>
            <button
              onClick={() => { closePdf(); ack(pdfView.policy); }}
              disabled={acking === pdfView.policy.id}
              data-testid="policy-pdf-ack-inline"
              className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white text-xs rounded-sm hover:bg-emerald-700 disabled:opacity-40 font-display font-bold uppercase tracking-wider"
            >
              <CheckCircle2 size={12} /> Acknowledge
            </button>
          </div>
          <div className="flex-1 bg-slate-900 overflow-hidden">
            {pdfView.loading ? (
              <div className="h-full flex items-center justify-center text-white">
                <Loader2 size={28} className="animate-spin" />
                <span className="ml-3 text-sm">Loading PDF…</span>
              </div>
            ) : (
              <iframe
                title={pdfView.policy.title}
                src={pdfView.blobUrl}
                className="w-full h-full border-0 bg-white"
                data-testid="policy-pdf-iframe"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// =====================================================================
// SUPER_ADMIN FLOW — 3 steps · workspace setup (unchanged from Iter 56)
// =====================================================================
function SuperAdminWizard({ user, onClose }) {
  const navigate = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  const [status, setStatus] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STATUS_KEY) || '{}'); }
    catch { return {}; }
  });

  const markDone = (key) => {
    const next = { ...status, [key]: true };
    setStatus(next);
    localStorage.setItem(STATUS_KEY, JSON.stringify(next));
  };

  useEffect(() => {
    Promise.all([
      apiGet('/settings/email').catch(() => null),
      apiGet('/hr/employees').catch(() => []),
    ]).then(([cfg, emps]) => {
      const next = { ...status };
      if (cfg?.smtp_host) next.email = true;
      const eList = Array.isArray(emps) ? emps : (emps?.employees || []);
      if (eList.length > 0) next.employee = true;
      setStatus(next);
      localStorage.setItem(STATUS_KEY, JSON.stringify(next));
      if (next.email && !next.employee) setStepIdx(1);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    // Persist server-side too so the wizard stays dismissed across devices /
    // cache-clears for super_admin (fire-and-forget; localStorage covers the rest).
    apiPut('/settings/onboarding', { admin_wizard_dismissed: true }).catch(() => {});
    onClose?.();
  };

  const STEPS = [
    { id: 'email', title: 'Email Setup', icon: Mail },
    { id: 'employee', title: 'Add First Employee', icon: Users },
  ];
  const completedCount = STEPS.filter(s => status[s.id]).length;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-start justify-center p-4 sm:p-8 overflow-y-auto" data-testid="onboarding-wizard">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-3xl my-auto border border-slate-200">
        <div className="flex items-start justify-between p-6 pb-4 border-b border-slate-200">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-display font-bold mb-1">Getting Started · {completedCount}/2</div>
            <h2 className="text-2xl font-display font-extrabold text-slate-800">Welcome to ELR Industries ERP</h2>
            <p className="text-sm text-slate-500 mt-1">Hi {user?.name || 'there'} — let's get your workspace ready in 2 quick steps.</p>
          </div>
          <button onClick={dismiss} data-testid="onboarding-close-btn" className="text-slate-400 hover:text-slate-700 p-1"><X size={20} /></button>
        </div>
        <StepBar steps={STEPS} stepIdx={stepIdx} setStepIdx={setStepIdx} status={status} />
        <div className="p-6">
          {stepIdx === 0 && <EmailStep done={status.email} onDone={() => { markDone('email'); setStepIdx(1); }} onSkip={() => setStepIdx(1)} />}
          {stepIdx === 1 && <EmployeeStep done={status.employee} onDone={() => { markDone('employee'); dismiss(); }} onSkip={dismiss} navigate={navigate} dismiss={dismiss} />}
        </div>
        <div className="flex items-center justify-between p-4 border-t border-slate-200 bg-slate-50 text-xs text-slate-500">
          <span>You can revisit this wizard from <strong>Settings → Relaunch Onboarding</strong>.</span>
          <button onClick={dismiss} data-testid="onboarding-dismiss-btn" className="text-slate-600 hover:text-slate-900 underline">Dismiss for now</button>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Shared bits
// =====================================================================
function StepBar({ steps, stepIdx, setStepIdx, status }) {
  return (
    <div className="flex border-b border-slate-200 bg-slate-50">
      {steps.map((s, i) => {
        const Icon = s.icon || Circle;
        return (
          <button
            key={s.id}
            onClick={() => setStepIdx(i)}
            data-testid={`onboarding-step-${s.id}`}
            className={`flex-1 px-3 py-3 text-left border-r border-slate-200 last:border-r-0 transition-colors ${
              stepIdx === i ? 'bg-white' : 'hover:bg-slate-100'
            }`}
          >
            <div className="flex items-center gap-2 mb-0.5">
              {status[s.id] ? (
                <CheckCircle2 size={14} className="text-emerald-600" />
              ) : (
                <Icon size={14} className={stepIdx === i ? 'text-[#1F7A6E]' : 'text-slate-300'} />
              )}
              <span className={`text-[9px] uppercase tracking-[0.16em] font-display font-bold ${stepIdx === i ? 'text-[#1F7A6E]' : 'text-slate-400'}`}>
                Step {i + 1}
              </span>
            </div>
            <div className={`text-xs font-semibold ${status[s.id] ? 'text-slate-500 line-through' : stepIdx === i ? 'text-slate-800' : 'text-slate-600'}`}>
              {s.title}
            </div>
          </button>
        );
      })}
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = 'text', testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.14em] font-display font-bold text-slate-500 mb-1">{label}</label>
      <input type={type} value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} className="input w-full" />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.14em] font-display font-bold text-slate-500 mb-1">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="input w-full">
        {options.map((o) => <option key={o} value={o}>{o || '— Select —'}</option>)}
      </select>
    </div>
  );
}

function ActionRow({ saving, done, saveLabel, onSave, onSkip, skipLabel = 'Skip for now' }) {
  return (
    <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
      <button onClick={onSkip} data-testid="onb-skip-btn" className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1 px-3 py-2">
        <SkipForward size={12} /> {skipLabel}
      </button>
      <button onClick={onSave} disabled={saving} data-testid="onb-save-btn"
        className="bg-[#1F7A6E] hover:bg-[#14504E] text-white font-display font-semibold text-sm px-4 py-2 rounded-sm flex items-center gap-2 disabled:opacity-50">
        {saving ? <Loader2 size={14} className="animate-spin" /> : null}
        {done ? 'Update' : saveLabel} <ArrowRight size={14} />
      </button>
    </div>
  );
}

// ───── Super_admin steps (unchanged Iter 56) ─────
function EmailStep({ done, onDone, onSkip }) {
  const [form, setForm] = useState({
    smtp_host: '', smtp_port: 587, smtp_user: '', smtp_password: '',
    from_email: '', from_name: 'ELR Industries Pvt Ltd', use_tls: true,
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testRecipient, setTestRecipient] = useState('');

  useEffect(() => {
    apiGet('/settings/email').then((cfg) => {
      if (cfg) setForm((f) => ({ ...f, ...cfg, smtp_password: '' }));
    }).catch(() => {});
  }, []);

  const save = async () => {
    if (!form.smtp_host || !form.smtp_user || !form.from_email) { toast.error('SMTP host, user, and from-email are required'); return; }
    setSaving(true);
    try {
      await apiPut('/settings/email', form);
      toast.success('Email config saved');
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testRecipient) { toast.error('Enter a recipient email to test'); return; }
    setTesting(true);
    try {
      await apiPost('/settings/email/test', { to: testRecipient });
      toast.success(`Test email sent to ${testRecipient}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Test failed — check SMTP credentials'); }
    finally { setTesting(false); }
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="text-sm font-semibold text-slate-800 mb-1">Configure SMTP for outgoing emails</div>
        <p className="text-xs text-slate-500">All HR notifications (offer letters, payslips, attendance alerts) and CRM emails will use this server. Most companies use their Gmail / Microsoft 365 / Zoho SMTP.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="SMTP Host *" value={form.smtp_host} onChange={(v) => setForm({ ...form, smtp_host: v })} placeholder="smtp.gmail.com" testid="onb-smtp-host" />
        <Field label="SMTP Port" type="number" value={form.smtp_port} onChange={(v) => setForm({ ...form, smtp_port: parseInt(v || 587, 10) })} testid="onb-smtp-port" />
        <Field label="SMTP User *" value={form.smtp_user} onChange={(v) => setForm({ ...form, smtp_user: v })} placeholder="hr@elloraepc.com" testid="onb-smtp-user" />
        <Field label="SMTP Password *" type="password" value={form.smtp_password} onChange={(v) => setForm({ ...form, smtp_password: v })} placeholder={done ? '••••••• (saved — leave blank to keep)' : 'App password / SMTP token'} testid="onb-smtp-pass" />
        <Field label="From Email *" value={form.from_email} onChange={(v) => setForm({ ...form, from_email: v })} placeholder="hr@elloraepc.com" testid="onb-from-email" />
        <Field label="From Name" value={form.from_name} onChange={(v) => setForm({ ...form, from_name: v })} testid="onb-from-name" />
      </div>
      <label className="flex items-center gap-2 text-xs text-slate-600">
        <input type="checkbox" checked={form.use_tls} onChange={(e) => setForm({ ...form, use_tls: e.target.checked })} data-testid="onb-tls" />
        Use TLS (recommended)
      </label>
      {done && (
        <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 flex items-center gap-2">
          <input type="email" placeholder="Send test email to..." value={testRecipient} onChange={(e) => setTestRecipient(e.target.value)} className="input flex-1" data-testid="onb-test-recipient" />
          <button onClick={sendTest} disabled={testing} data-testid="onb-test-send-btn" className="btn-secondary flex items-center gap-1 text-xs">
            {testing ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />} Send Test
          </button>
        </div>
      )}
      <ActionRow saving={saving} done={done} saveLabel="Save & Continue" onSave={save} onSkip={onSkip} />
    </div>
  );
}

function EmployeeStep({ done, onDone, onSkip, navigate, dismiss }) {
  const [form, setForm] = useState({
    emp_id: '', name: '', designation: '', department: 'Admin',
    email: '', phone: '', doj: new Date().toISOString().slice(0, 10),
    basic: 0, hra: 0, allowances: 0,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.emp_id || !form.name) { toast.error('Employee ID and Name are required'); return; }
    if (!(form.email || '').trim()) { toast.error('Email is required to auto-create login'); return; }
    setSaving(true);
    try {
      await apiPost('/hr/employees', form);
      toast.success(`Employee ${form.name} added`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to add employee'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="text-sm font-semibold text-slate-800 mb-1">Add your first employee</div>
        <p className="text-xs text-slate-500">You can either add one employee here, or jump to the full Employee Master for the bulk-import wizard.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Employee Code *" value={form.emp_id} onChange={(v) => setForm({ ...form, emp_id: v })} placeholder="EE-001" testid="onb-emp-id" />
        <Field label="Full Name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Jane Doe" testid="onb-emp-name" />
        <Field label="Designation" value={form.designation} onChange={(v) => setForm({ ...form, designation: v })} placeholder="Project Manager" testid="onb-emp-desig" />
        <SelectField label="Department" value={form.department} onChange={(v) => setForm({ ...form, department: v })} testid="onb-emp-dept"
          options={['Admin', 'HR', 'IT', 'MD', 'Business Development', 'Tendering', 'Engineering', 'Finance & Accounts', 'Projects/Site', 'Quality', 'Safety', 'Contracts & Legal', 'Procurement']} />
        <Field label="Email *" value={form.email} onChange={(v) => setForm({ ...form, email: v })} placeholder="jane@elloraepc.com" testid="onb-emp-email" />
        <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} placeholder="+91 9876543210" testid="onb-emp-phone" />
        <Field label="Date of Joining" type="date" value={form.doj} onChange={(v) => setForm({ ...form, doj: v })} testid="onb-emp-doj" />
        <Field label="Basic Pay (₹)" type="number" value={form.basic} onChange={(v) => setForm({ ...form, basic: parseFloat(v || 0) })} testid="onb-emp-basic" />
      </div>
      <div className="flex items-center justify-between">
        <button onClick={() => { dismiss(); navigate('/app/hr/employees'); }} data-testid="onb-emp-bulk-btn" className="text-xs text-[#1F7A6E] hover:underline flex items-center gap-1">
          Or go to full Employee Master · Bulk Import <ArrowRight size={12} />
        </button>
      </div>
      <ActionRow saving={saving} done={done} saveLabel="Add Employee & Continue" onSave={save} onSkip={onSkip} />
    </div>
  );
}

